import { useSuspenseQueries } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { policyControllerPlanTasksQueryOptions, type PlannedTaskDto } from 'korint-sdk';
import { useFeatureFlag } from 'korint-sdk/react';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { useUserPermissions } from './useUserPermissions';

export type PlannedRecoveryTask = { whenAt: string; task: PlannedTaskDto };

export function usePlannedRecoveryTasks() {
  const { policy } = usePolicyOnDashboard();
  const automaticRecoveryEnabled = useFeatureFlag('feat_automatic_unpaid_retry', policy.productId);
  const { can } = useUserPermissions({
    productId: policy.productId,
    actions: ['PLAN_POLICY_TASKS'],
  });
  const canViewRecoveryPlan = automaticRecoveryEnabled && can.PLAN_POLICY_TASKS;

  const [taskPlansQuery] = useSuspenseQueries({
    queries: (canViewRecoveryPlan ? [policy.id] : []).map(policyId =>
      policyControllerPlanTasksQueryOptions(policyId, {
        from: dayjs().format('YYYY-MM-DD'),
        days: 91,
      }),
    ),
  });

  const failure = taskPlansQuery?.data.failures.find(({ policyId }) => policyId === policy.id);
  if (failure) throw new Error(failure.reason);

  const recoveryTasks: PlannedRecoveryTask[] = (taskPlansQuery?.data.plans ?? [])
    .filter(({ policyId }) => policyId === policy.id)
    .flatMap(({ whenAt, tasks }) =>
      tasks
        .filter(({ task }) =>
          ['unpaid-retry', 'unpaid-reminder', 'unpaid-escalation'].includes(task),
        )
        .map(task => ({ whenAt, task })),
    )
    .sort((left, right) => Date.parse(left.whenAt) - Date.parse(right.whenAt));

  return { recoveryTasks, refetch: taskPlansQuery?.refetch };
}
